import React, {useCallback, useEffect, useState} from 'react';

import uniqueId from 'lodash.uniqueid';
import {FilterInput} from 'src/components/input';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {RESPONSE_NOTE_LIFETIME} from 'src/contexts/background-task-context/background-task-context-provider';
import {LIFECYCLE_STATUS_OPTIONS} from 'src/modules/profile/account/tabs/product-access/constant';
import {NewAccessTermBtn} from 'src/modules/profile/account/tabs/product-access/new-access-term-btn';
import {searchAccountProductSubscriptions} from 'src/openapi/admin/apis/AccountProductSubscriptionsApi';
import {ProductSubscriptionLifecycleStatus} from 'src/openapi/admin/models/ProductSubscriptionLifecycleStatus';
import {
    ProductSubscriptionSortDirection,
    ProductSubscriptionSortField,
} from 'src/openapi/admin/models/ProductSubscriptionSort';
import {useAppDispatch} from 'src/store/hooks';

import {BoxLoading} from '@pb/kit-component-box-loading';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {Group} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';

import {ProductAccessTable} from './product-access-table';

import {
    productAccessHeader,
} from './product-access.module.styl';

import type {Error} from 'src/openapi/admin/models/Error';
import type {ProductSubscription} from 'src/openapi/admin/models/ProductSubscription';


type Props = {accountId: number};

export const ProductAccessTab: React.FC<Props> = ({accountId}) => {
    const apiDispatch = useAppDispatch();
    const [statuses, setStatuses] = useState<ProductSubscriptionLifecycleStatus[]>([
        ProductSubscriptionLifecycleStatus.ACTIVE,
        ProductSubscriptionLifecycleStatus.PENDING,
    ]);
    const [externalKey, setExternalKey] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [sort, setSort] = useState({
        field: ProductSubscriptionSortField.startDate,
        direction: ProductSubscriptionSortDirection.ASC,
    });
    const [subscriptions, setSubscriptions] = useState<ProductSubscription[] | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [error, setError] = useState<Error | null>(null);
    const [refreshVersion, setRefreshVersion] = useState(0);
    const {onCreateNote} = usePushNotification();

    useEffect(() => {
        const abortController = new AbortController();
        setSubscriptions(null);
        setError(null);
        apiDispatch(searchAccountProductSubscriptions({
            accountId,
            productSubscriptionSearchRequest: {
                filters: {
                    ...(statuses.length ? {lifecycleStatuses: statuses} : {}),
                    ...(externalKey ? {externalKey} : {}),
                },
                pagination: {page, pageSize},
                sort,
            },
        }, {signal: abortController.signal})).then(response => {
            if (abortController.signal.aborted) {
                return;
            }
            if (response.payload?.success) {
                const responseTotalCount = response.payload.data?.totalCount || 0;

                setSubscriptions(response.payload.data?.content || []);
                setTotalCount(responseTotalCount);
            } else {
                setError(response.payload?.data || null);
            }
        });
        return () => abortController.abort();
    }, [accountId, apiDispatch, externalKey, page, pageSize, refreshVersion, sort, statuses]);

    const changeStatuses = useCallback((nextStatuses: ProductSubscriptionLifecycleStatus[]) => {
        setStatuses(nextStatuses);
        setPage(1);
    }, []);
    const changeExternalKey = useCallback((value: string) => {
        setExternalKey(value);
        setPage(1);
    }, []);
    const clearExternalKey = useCallback(() => changeExternalKey(''), [changeExternalKey]);
    const changePageSize = useCallback((nextPageSize: number) => {
        setPageSize(nextPageSize);
        setPage(1);
    }, []);
    const changeSort = useCallback(
        (field: ProductSubscriptionSortField, direction: ProductSubscriptionSortDirection) => {
            setSort({field, direction});
            setPage(1);
        }, []);
    const refreshSubscriptions = useCallback((message: string) => {
        setRefreshVersion(version => version + 1);
        onCreateNote({
            id: uniqueId('product-access-'),
            status: Status.SUCCESS,
            buttonProps: {iconProps: {type: Icons.CLOSE}},
            autoHideTimeout: RESPONSE_NOTE_LIFETIME,
            body: () => message,
        });
    }, [onCreateNote]);

    return (
        <ServerResponseHandler serverError={error} sectionName="Product Access">
            <div>
                <Group justify="space-between" alignItems="center" className={productAccessHeader}>
                    <Group>
                        <FilterInput
                            id="product-access-search"
                            width={300}
                            maxLength={1024}
                            value={externalKey}
                            placeholder="Search by product name"
                            onChange={changeExternalKey}
                            onClear={clearExternalKey}
                            timeOut={1000}
                        />
                        <div style={{width: 300}}>
                            <ComboBox
                                value={statuses}
                                placeholder="Select lifecycle status"
                                onChange={changeStatuses}>
                                {LIFECYCLE_STATUS_OPTIONS.map(([value, caption]) => (
                                    <ComboboxItem key={value} value={value} caption={caption}/>
                                ))}
                            </ComboBox>
                        </div>
                    </Group>
                    <NewAccessTermBtn
                        accountId={accountId}
                        onCreated={() => refreshSubscriptions('Product Access Term has been created.')}
                    />
                </Group>
                <BoxLoading loading={subscriptions === null}>
                    <ProductAccessTable
                        subscriptions={subscriptions || []}
                        totalCount={totalCount}
                        page={page}
                        pageSize={pageSize}
                        sort={sort}
                        statuses={statuses}
                        onPageChange={setPage}
                        onPageSizeChange={changePageSize}
                        onSortChange={changeSort}
                        onUpdated={() => refreshSubscriptions('Product Access Term has been updated.')}
                        accountId={accountId}
                    />
                </BoxLoading>
            </div>
        </ServerResponseHandler>
    );
};
